<template>
    <div>
        <Modal size="xxl" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">{{ $t("vue.your_order") }}</div>
            </template>

            <template #body>
                <div class="bg-white p-6">
                    <div
                        class="flex flex-col items-stretch justify-between md:flex-row"
                    >
                        <div
                            class="relative mt-6 h-full w-full rounded-md border border-[#BEE3F8] bg-[#EBF8FF] px-6 pb-5 pt-6 md:ml-2 md:mt-0 md:w-1/2 md:px-9 lg:ml-4 lg:w-full lg:max-w-[418px]"
                        >
                            <div
                                v-if="!checkoutLoaded"
                                class="absolute inset-0 h-full w-full bg-black bg-opacity-5"
                            >
                                <div
                                    class="flex h-full items-center justify-center"
                                >
                                    <LoadingSpinner
                                        size="medium"
                                        :delay="false"
                                    />
                                </div>
                            </div>

                            <h4
                                class="text-lg font-medium leading-[18px] tracking-tighter text-gray-800"
                            >
                                {{ $t("vue.summary") }}
                            </h4>

                            <p
                                class="mt-4 max-w-sm text-sm font-medium text-gray-800"
                            >
                                {{ $t("vue.subscription") }} -
                                {{ selectedPlan[0].productName }}
                            </p>

                            <div
                                class="flex items-start justify-between pb-[14px]"
                            >
                                <div>
                                    <p
                                        class="mt-[3px] text-sm leading-normal text-gray-500"
                                    >
                                        <span>
                                            {{ domainsCount }}
                                            {{ $t("vue.domains") }}
                                        </span>
                                    </p>

                                    <p
                                        class="mt-2 text-sm leading-normal text-gray-500"
                                    >
                                        {{
                                            period === "yearly"
                                                ? $t("vue.annually")
                                                : $t("vue.monthly")
                                        }}
                                        {{ $t("vue.plan_renews") }}

                                        {{
                                            format(
                                                new Date(date),
                                                "MMMM d, yyyy",
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>

                            <div
                                class="flex flex-col gap-1 border-t border-[#BEE3F8] pt-5"
                            >
                                <div class="flex justify-between">
                                    <div
                                        class="text-md font-medium leading-normal tracking-tighter text-gray-700"
                                    >
                                        {{ $t("vue.subtotal") }}
                                    </div>
                                    <div
                                        v-if="subtotal"
                                        class="text-md font-medium text-gray-700"
                                    >
                                        ${{ subtotal }}
                                    </div>
                                </div>

                                <div class="flex justify-between">
                                    <div
                                        class="text-md font-medium leading-normal tracking-tighter text-gray-700"
                                    >
                                        {{ $t("vue.tax") }}
                                    </div>
                                    <div
                                        v-if="tax"
                                        class="text-md font-medium text-gray-700"
                                    >
                                        ${{ tax }}
                                    </div>
                                </div>

                                <div class="flex justify-between">
                                    <div
                                        class="text-lg font-medium leading-normal tracking-tighter text-gray-800"
                                    >
                                        {{ $t("vue.total_due_today") }}
                                    </div>
                                    <div
                                        class="text-lg font-bold text-gray-800"
                                    >
                                        ${{ total }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="w-full rounded-md border border-gray-300 px-6 pb-5 pt-6 md:w-1/2 lg:w-full lg:max-w-[462px]"
                        >
                            <div class="checkout-container"></div>
                        </div>
                    </div>
                </div>
            </template>
        </Modal>

        <SubscriptionBuyErrorModal
            v-if="errorModal"
            :message="errorModalMessage"
            @close="reopenOrderModal"
        />

        <SubscriptionBuySuccessModal
            v-if="successModal"
            @close="handleCloseSuccessModal"
        />
    </div>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import { add, format } from "date-fns";
import { onMounted } from "vue";
import { initializePaddle, type Paddle } from "@paddle/paddle-js";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const globalProps = computed(() => usePage<IRhizomPageProps>().props);

var myPaddleInstance: Paddle | null = null;

const props = defineProps({
    domainsCount: {
        type: Number,
        default: () => 0,
    },
    paddleClientToken: {
        type: String,
        default: "",
    },
    paddlePriceId: {
        type: String,
        default: "",
    },
    paddleEnvironment: {
        type: String as () => "production" | "sandbox",
        default: "production",
    },
    period: {
        type: String as PropType<"monthly" | "yearly">,
        default: "monthly",
    },
    selectedPlan: {
        type: Array as () => {
            productName: string;
            productDescription: string;
            type: "monthly" | "yearly";
            priceId: string;
            productId: string;
            totalPrice?: number;
        }[],
        default: () => [],
    },
});

const emit = defineEmits(["close", "reopen"]);
const checkoutLoaded = ref(false);
const planName = ref("");
const total = ref(0);
const subtotal = ref(0);
const tax = ref(0);
const errorModal = ref(false);
const errorModalMessage = ref("");
const successModal = ref(false);

onMounted(() => {
    paddleInit();
});

const date = computed(() => {
    if (props.period === "monthly") {
        return add(new Date(), {
            months: 1,
        });
    } else return add(new Date(), { years: 1 });
});

const paddleInit = () => {
    initializePaddle({
        environment: props.paddleEnvironment,
        token: props.paddleClientToken,
        pwCustomer: {
            id: globalProps.value.team.uuid.toLowerCase(),
            email: globalProps.value.user.email,
        },
        checkout: {
            settings: {
                displayMode: "inline",
                frameTarget: "checkout-container",
                frameInitialHeight: 450,
                frameStyle:
                    "width: 100%; min-width: 312px; background-color: transparent; border: none;",
                showAddDiscounts: true,
            },
        },

        eventCallback: (event) => {
            handlePaddleResponse(event);
        },
    }).then((paddleInstance: Paddle | undefined) => {
        if (paddleInstance) {
            myPaddleInstance = paddleInstance;
            openCheckout();
            console.log(paddleInstance);
        }
    });
};

const openCheckout = () => {
    myPaddleInstance?.Checkout.open({
        items: [
            {
                priceId:
                    props.period === "yearly"
                        ? props.selectedPlan[1].priceId
                        : props.selectedPlan[0].priceId,
                quantity: props.domainsCount,
            },
        ],
        customer: {
            email: globalProps.value.user.email,
        },
        customData: {
            team: globalProps.value.team.uuid.toLowerCase(),
        },
        settings: {
            showAddDiscounts: true,
        },
    });
};

const handlePaddleResponse = async (event: any) => {
    if (event.name === "checkout.loaded") {
        checkoutLoaded.value = true;

        updateTotalsAndPlanName(event.data);
    }

    if (
        event.name === "checkout.customer.updated" ||
        event.name === "checkout.items.updated"
    ) {
        updateTotalsAndPlanName(event.data);
    }

    if (event.name === "checkout.error") {
        openErrorModal(event.data.message);
    }

    if (event.name === "checkout.completed") {
        successModal.value = true;
    }
};

const updateTotalsAndPlanName = (data) => {
    planName.value = data.items[0].price_name;
    total.value = data.totals.total;
    subtotal.value = data.totals.subtotal;
    tax.value = data.totals.tax;
};

const handleCloseSuccessModal = () => {
    emit("close");
};

const reopenOrderModal = () => {
    emit("reopen");
};

const openErrorModal = (message: string) => {
    errorModal.value = true;
    errorModalMessage.value = message;
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
